import { apiClient } from './apiClient';

export type ProjectFile = {
    id: string;
    file: string; // URL
    thumbnail?: string;
    original_name: string;
    file_type: string;
}

export type Project = {
  id: string;
  name: string;
  description: string;
  files?: ProjectFile[];
}

type ProjectsResponse = {
    projects: Project[];
    count: number;
    next: string | null;
    previous: string | null;
}

export async function getProjects(url = "/api/projects/"): Promise<ProjectsResponse> {
    const resolvedUrl = url.startsWith('http') ? url : `${import.meta.env.VITE_API_URL}${url}`;
    const response = await apiClient.get(resolvedUrl);
    return {
        projects: response.data.results,
        count: response.data.count,
        next: response.data.next,
        previous: response.data.previous,
    }
}

export async function createProject(name: string, description: string = "", files?: File[]) : Promise<Project> {
    const fullUrl = `${import.meta.env.VITE_API_URL}/api/projects/`;
    
    let body: any;
    let headers: any = {};

    if (files && files.length > 0) {
        const formData = new FormData();
        formData.append('name', name);
        formData.append('description', description);
        files.forEach(file => {
            formData.append('files', file);
        });
        body = formData;
        // Axios handles multipart/form-data headers automatically for FormData
    } else {
        body = { 
            name: name,
            description: description
        };
    }

    const response = await apiClient.post(fullUrl, body, { headers });
    return response.data;
}

export async function updateProject(projectId: string, name?: string, description?: string, files?: File[]): Promise<Project> {
    const fullUrl = `${import.meta.env.VITE_API_URL}/api/projects/${projectId}/`;
    
    let body: any;
    let headers: any = {};

    if (files && files.length > 0) {
        const formData = new FormData();
        if (name !== undefined) formData.append('name', name);
        if (description !== undefined) formData.append('description', description);
        files.forEach(file => {
            formData.append('files', file);
        });
        body = formData;
    } else {
        body = {};
        if (name !== undefined) body.name = name;
        if (description !== undefined) body.description = description;
    }

    const response = await apiClient.patch(fullUrl, body, { headers });
    return response.data;
}

export async function deleteProjects(projectIds: string[]): Promise<void> {
    if (projectIds.length === 0) {
        return;
    }

    const baseUrl = `${import.meta.env.VITE_API_URL}/api/projects/`;
    await Promise.all(
        projectIds.map(projectId =>
            apiClient.delete(
                `${baseUrl}${projectId}/`, 
            )
        )
    );
}

export async function renameProject(projectId: string, name: string): Promise<Project> {
    return updateProject(projectId, name);
}

export async function deleteProjectFile(fileId: string): Promise<void> {
    const fullUrl = `${import.meta.env.VITE_API_URL}/api/files/${fileId}/`;
    await apiClient.delete(fullUrl);
}